import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useFees } from '../context/FeeContext';
import SetupChecklist from '../components/SetupChecklist';
import { GOODJOB_LOGO_BASE64 } from '../assets/logoBase64';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { feeRates } = useFees();
  const [platformStats, setPlatformStats] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [allWithdrawals, setAllWithdrawals] = useState([]);
  const [actionableItems, setActionableItems] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: '', end: '', preset: 'all' });
  const [revenueDetails, setRevenueDetails] = useState({ details: [], summary: {} });
  const [withdrawalDateRange, setWithdrawalDateRange] = useState({ start: '', end: '', preset: 'all' });
  const [analyticsSnapshot, setAnalyticsSnapshot] = useState(null);
  const [feeHistory, setFeeHistory] = useState([]);

  const getDatePreset = (preset) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split('T')[0];
    switch (preset) {
      case 'today': return { start: fmt(today), end: fmt(today), preset: 'today' };
      case 'week': { const s = new Date(today); s.setDate(today.getDate() - today.getDay()); return { start: fmt(s), end: fmt(today), preset: 'week' }; }
      case 'month': return { start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(today), preset: 'month' };
      case 'quarter': return { start: fmt(new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1)), end: fmt(today), preset: 'quarter' };
      case 'year': return { start: fmt(new Date(today.getFullYear(), 0, 1)), end: fmt(today), preset: 'year' };
      default: return { start: '', end: '', preset: 'all' };
    }
  };

  useEffect(() => {
    fetchAdminData();
    markWithdrawalNotificationsRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end]);

  const markWithdrawalNotificationsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-read-by-type`, {
        type: 'withdrawal_request'
      });
      // Refresh badge counts in navbar
      window.dispatchEvent(new Event('refreshBadgeCounts'));
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const fetchAdminData = async () => {
    try {
      const dateParams = dateRange.start && dateRange.end
        ? `?start_date=${dateRange.start}&end_date=${dateRange.end}`
        : '';
      const [statsRes, withdrawalsRes, allWithdrawalsRes, actionableRes, revenueRes, analyticsRes, feeHistoryRes] = await Promise.all([
        axios.get(`${API}/admin/stats${dateParams}`),
        axios.get(`${API}/withdrawals/pending`),
        axios.get(`${API}/withdrawals/all`),
        axios.get(`${API}/admin/actionable-items`).catch(() => ({ data: { total: 0, items: [] } })),
        axios.get(`${API}/admin/revenue-details${dateParams}`),
        axios.get(`${API}/admin/analytics`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/fee-history`).catch(() => ({ data: [] }))
      ]);

      setPlatformStats(statsRes.data);
      setWithdrawals(withdrawalsRes.data);
      setAllWithdrawals(allWithdrawalsRes.data);
      setActionableItems(actionableRes.data || { total: 0, items: [] });
      setRevenueDetails(revenueRes.data || { details: [], summary: {}, next_cursor: null, has_more: false });
      setAnalyticsSnapshot(analyticsRes.data);
      setFeeHistory(feeHistoryRes.data || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const revenueDateParams = () => (dateRange.start && dateRange.end
    ? `?start_date=${dateRange.start}&end_date=${dateRange.end}`
    : '');

  // Full dataset for exports (follows pagination server-side via fetch_all).
  const fetchAllRevenueDetails = async () => {
    const base = revenueDateParams();
    const sep = base ? '&' : '?';
    try {
      const res = await axios.get(`${API}/admin/revenue-details${base}${sep}fetch_all=true`);
      return res.data?.details || [];
    } catch (e) {
      console.error('Error fetching full revenue details:', e);
      return revenueDetails.details || [];
    }
  };

  const [loadingMoreRevenue, setLoadingMoreRevenue] = useState(false);
  const loadMoreRevenue = async () => {
    if (!revenueDetails.next_cursor || loadingMoreRevenue) return;
    setLoadingMoreRevenue(true);
    const base = revenueDateParams();
    const sep = base ? '&' : '?';
    try {
      const res = await axios.get(`${API}/admin/revenue-details${base}${sep}cursor=${encodeURIComponent(revenueDetails.next_cursor)}`);
      setRevenueDetails(prev => ({
        ...res.data,
        details: [...(prev.details || []), ...(res.data.details || [])],
      }));
    } catch (e) {
      console.error('Error loading more revenue rows:', e);
    } finally {
      setLoadingMoreRevenue(false);
    }
  };

  const downloadRevenuePDF = async () => {
    const doc = new jsPDF();
    const rangeLabel = dateRange.preset === 'all' ? 'All Time' : `${dateRange.start} to ${dateRange.end}`;
    const allDetails = await fetchAllRevenueDetails();
    
    // Add GoodJob logo
    try {
      doc.addImage(GOODJOB_LOGO_BASE64, 'PNG', 14, 8, 18, 18);
    } catch (e) { /* fallback if logo fails */ }
    
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text('GoodJob PH - Platform Revenue Report', 36, 20);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Date Range: ${rangeLabel}`, 36, 28);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 36, 34);
    
    // Summary
    const s = revenueDetails.summary || {};
    doc.setFontSize(12);
    doc.setTextColor(40, 40, 40);
    doc.text('Summary', 14, 44);
    doc.setFontSize(10);
    doc.text(`Total Bookings: ${s.total_bookings || 0}`, 14, 52);
    doc.text(`Total Service Amount: PHP ${(s.total_service_amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`, 14, 58);
    doc.text(`Client Fees Collected: PHP ${(s.total_client_fees || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`, 14, 64);
    doc.text(`Provider Fees Collected: PHP ${(s.total_provider_fees || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`, 14, 70);
    doc.setFontSize(11);
    doc.setTextColor(0, 100, 0);
    doc.text(`Total Platform Revenue: PHP ${(s.total_platform_revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`, 14, 78);

    // Table using autoTable function directly
    if (allDetails.length > 0) {
      autoTable(doc, {
        startY: 86,
        head: [['Date', 'Order', 'Provider', 'Service', 'Svc Amt', 'Client Fee', 'Provider Fee', 'Platform Rev']],
        body: allDetails.map(d => [
          d.scheduled_date || d.date?.substring(0, 10) || '',
          d.booking_order_code || d.booking_id?.substring(0, 8) || '',
          d.provider_name,
          d.service_title,
          `${d.service_amount.toFixed(2)}`,
          `${d.client_fee.toFixed(2)} (${d.client_fee_rate || ''}%)`,
          `${d.provider_fee.toFixed(2)} (${d.provider_fee_rate || ''}%)`,
          `${d.platform_revenue.toFixed(2)}`,
        ]),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [37, 99, 235], fontSize: 7 },
        alternateRowStyles: { fillColor: [245, 247, 250] },
      });
    }

    doc.save(`GoodJobPH_Revenue_${rangeLabel.replace(/\s/g, '_')}.pdf`);
  };

  const downloadRevenueXLSX = async () => {
    const rangeLabel = dateRange.preset === 'all' ? 'All Time' : `${dateRange.start} to ${dateRange.end}`;
    const s = revenueDetails.summary || {};
    const allDetails = await fetchAllRevenueDetails();
    
    // Build header rows
    const headerRows = [
      ['GoodJob PH - Platform Revenue Report'],
      [`Date Range: ${rangeLabel}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
      ['Summary'],
      ['Total Bookings', s.total_bookings || 0],
      ['Total Service Amount', s.total_service_amount || 0],
      ['Client Fees Collected', s.total_client_fees || 0],
      ['Provider Fees Collected', s.total_provider_fees || 0],
      ['Total Platform Revenue', s.total_platform_revenue || 0],
      [],
      ['Date', 'Order Code', 'Provider', 'Client', 'Service', 'Service Amount', 'Client Fee', 'Client Fee %', 'Provider Fee', 'Provider Fee %', 'Platform Revenue'],
    ];

    const dataRows = (allDetails || []).map(d => [
      d.scheduled_date || d.date?.substring(0, 10) || '',
      d.booking_order_code || d.booking_id?.substring(0, 8) || '',
      d.provider_name,
      d.client_name,
      d.service_title,
      d.service_amount,
      d.client_fee,
      `${d.client_fee_rate || ''}%`,
      d.provider_fee,
      `${d.provider_fee_rate || ''}%`,
      d.platform_revenue,
    ]);

    const allRows = [...headerRows, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(allRows);

    // Set column widths
    ws['!cols'] = [
      { wch: 14 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 28 },
      { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 16 },
    ];

    // Merge title cell
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 10 } }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Revenue Report');
    XLSX.writeFile(wb, `GoodJobPH_Revenue_${rangeLabel.replace(/\s/g, '_')}.xlsx`);
  };


  const handleWithdrawalAction = async (withdrawalId, action) => {
    try {
      if (action === 'approved') {
        await axios.put(`${API}/withdrawals/${withdrawalId}/approve`);
      } else if (action === 'rejected') {
        await axios.put(`${API}/withdrawals/${withdrawalId}/reject`);
      }
      // Refresh badge counts immediately after action
      window.dispatchEvent(new Event('refreshBadgeCounts'));
      fetchAdminData();
    } catch (error) {
      console.error('Error updating withdrawal:', error);
      alert(error.response?.data?.detail || 'Failed to update withdrawal');
    }
  };

  const handleMarkPaid = async (withdrawalId) => {
    const reference = window.prompt(
      'Confirm you have MANUALLY sent this payout (via GCash/Maya/Bank).\n\nEnter the transaction reference number (optional):'
    );
    if (reference === null) return; // cancelled
    try {
      await axios.put(`${API}/withdrawals/${withdrawalId}/mark-paid`, null, {
        params: reference ? { reference_number: reference } : {},
      });
      window.dispatchEvent(new Event('refreshBadgeCounts'));
      fetchAdminData();
    } catch (error) {
      console.error('Error marking withdrawal as paid:', error);
      alert(error.response?.data?.detail || 'Failed to mark withdrawal as paid');
    }
  };

  const handleMarkFailed = async (withdrawalId) => {
    const reason = window.prompt(
      'Mark this payout as FAILED?\n\nThis refunds the amount back to the provider\'s available balance (use only if the money did NOT actually leave the platform).\n\nReason (optional):'
    );
    if (reason === null) return; // cancelled
    try {
      await axios.put(`${API}/withdrawals/${withdrawalId}/mark-failed`, null, {
        params: reason ? { reason } : {},
      });
      window.dispatchEvent(new Event('refreshBadgeCounts'));
      fetchAdminData();
    } catch (error) {
      console.error('Error marking withdrawal as failed:', error);
      alert(error.response?.data?.detail || 'Failed to mark withdrawal as failed');
    }
  };

  const handleSyncStatus = async (withdrawalId) => {
    try {
      const res = await axios.put(`${API}/withdrawals/${withdrawalId}/sync`);
      alert(res.data?.message || 'Status synced');
      window.dispatchEvent(new Event('refreshBadgeCounts'));
      fetchAdminData();
    } catch (error) {
      console.error('Error syncing withdrawal status:', error);
      alert(error.response?.data?.detail || 'Failed to sync status with PayMongo');
    }
  };

  // Handle click on actionable items - navigate to appropriate page
  const handleActionableItemClick = (item) => {
    switch (item.type) {
      case 'kyc_review':
        // Navigate to ID verification page with the user ID
        navigate('/admin/id-verification', { state: { userId: item.user_id } });
        break;
      case 'withdrawal':
        // Scroll to pending withdrawals section on this page
        document.getElementById('pending-withdrawals')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 'ban_appeal':
        // Navigate to user management with appeals filter
        navigate('/owner', { state: { tab: 'users', filter: 'appeals' } });
        break;
      case 'dispute':
        // Navigate to dispute detail page directly
        if (item.dispute_id) {
          navigate(`/disputes/${item.dispute_id}`);
        } else {
          navigate('/owner', { state: { tab: 'disputes' } });
        }
        break;
      default:
        console.log('Unknown action type:', item.type);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-semibold text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold mb-8 bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent" data-testid="page-title">
          Platform Admin Dashboard
        </h1>

        <SetupChecklist />

        {/* Platform Stats */}
        {platformStats && (
          <>
          {/* Date Range Filter */}
          <div className="bg-white p-4 rounded-lg shadow-md mb-6" data-testid="date-filter">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-semibold text-gray-700">Filter by:</span>
              <div className="flex flex-wrap gap-1 bg-gray-100 rounded-lg p-1">
                {[
                  { key: 'all', label: 'All Time' },
                  { key: 'today', label: 'Today' },
                  { key: 'week', label: 'This Week' },
                  { key: 'month', label: 'This Month' },
                  { key: 'quarter', label: 'Quarter' },
                  { key: 'year', label: 'This Year' },
                ].map(p => (
                  <button
                    key={p.key}
                    onClick={() => setDateRange(getDatePreset(p.key))}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition whitespace-nowrap ${
                      dateRange.preset === p.key
                        ? 'bg-blue-600 text-white shadow'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`filter-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ start: e.target.value, end: dateRange.end || e.target.value, preset: 'custom' })}
                  className="border rounded-md px-2 py-1.5 text-xs"
                  data-testid="filter-start-date"
                />
                <span className="text-gray-400 text-xs">to</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ start: dateRange.start || e.target.value, end: e.target.value, preset: 'custom' })}
                  className="border rounded-md px-2 py-1.5 text-xs"
                  data-testid="filter-end-date"
                />
              </div>
              {dateRange.preset !== 'all' && (
                <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                  {dateRange.start} - {dateRange.end}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="platform-stats">
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
              <h3 className="text-gray-500 text-sm font-medium">Total Users</h3>
              <p className="text-3xl font-bold text-blue-600 mt-2">{platformStats.total_users}</p>
              <p className="text-xs text-gray-500 mt-1">
                {platformStats.total_providers} Providers • {platformStats.total_clients} Clients
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
              <h3 className="text-gray-500 text-sm font-medium">Platform Revenue ({parseFloat(feeRates.total_platform_rate).toFixed(1)}%)</h3>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {platformStats.currency || 'PHP'} {platformStats.total_revenue?.toFixed(2) || '0.00'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Commission earned</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
              <h3 className="text-gray-500 text-sm font-medium">Total Transaction Volume</h3>
              <p className="text-3xl font-bold text-purple-600 mt-2">
                {platformStats.currency || 'PHP'} {platformStats.total_transactions?.toFixed(2) || '0.00'}
              </p>
              <p className="text-xs text-gray-500 mt-1">All transactions</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-yellow-500">
              <h3 className="text-gray-500 text-sm font-medium">Pending Withdrawals</h3>
              <p className="text-3xl font-bold text-yellow-600 mt-2">{platformStats.pending_withdrawals}</p>
              <p className="text-xs text-gray-500 mt-1">Awaiting approval</p>
            </div>
          </div>
          </>
        )}

        {/* Analytics Snapshot */}
        {analyticsSnapshot && (
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-6 rounded-xl shadow-lg mb-8 border border-gray-700" data-testid="analytics-snapshot">
            <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
              <div>
                <h2 className="text-lg font-bold text-white">Financial Analytics</h2>
                <p className="text-sm text-gray-400">Quick insights from your platform performance</p>
              </div>
              <button
                onClick={() => navigate('/admin/analytics')}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition"
                data-testid="view-analytics-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                View Full Analytics
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700/50">
                <p className="text-xs text-gray-500 mb-1">MoM Growth</p>
                <p className={`text-xl font-bold ${analyticsSnapshot.summary.mom_growth !== null && analyticsSnapshot.summary.mom_growth >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {analyticsSnapshot.summary.mom_growth !== null ? `${analyticsSnapshot.summary.mom_growth > 0 ? '+' : ''}${analyticsSnapshot.summary.mom_growth}%` : 'N/A'}
                </p>
              </div>
              <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700/50">
                <p className="text-xs text-gray-500 mb-1">Top Category</p>
                <p className="text-sm font-bold text-blue-400 truncate">{analyticsSnapshot.top_categories?.[0]?.category || 'N/A'}</p>
                <p className="text-xs text-gray-500">{analyticsSnapshot.top_categories?.[0]?.count || 0} bookings</p>
              </div>
              <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700/50">
                <p className="text-xs text-gray-500 mb-1">Top Provider</p>
                <p className="text-sm font-bold text-green-400 truncate">{analyticsSnapshot.top_providers?.[0]?.provider_name || 'N/A'}</p>
                <p className="text-xs text-gray-500">PHP {(analyticsSnapshot.top_providers?.[0]?.earnings || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
              </div>
              <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700/50">
                <p className="text-xs text-gray-500 mb-1">Completion Rate</p>
                <p className="text-xl font-bold text-cyan-400">{analyticsSnapshot.summary.completion_rate || 0}%</p>
              </div>
            </div>
          </div>
        )}

        {/* Admin Actionable Items - Shows what's triggering the red dot */}
        {actionableItems.total > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-8 border-l-4 border-red-500" data-testid="actionable-items">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
              Items Requiring Your Attention ({actionableItems.total})
            </h2>
            <div className="space-y-3">
              {actionableItems.items.filter(item => item.type !== 'withdrawal').map((item, index) => (
                <div 
                  key={index} 
                  onClick={() => handleActionableItemClick(item)}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 hover:border-gray-300 transition-all"
                  data-testid={`actionable-item-${index}`}
                >
                  <div className="flex items-start sm:items-center gap-3 min-w-0">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap shrink-0 ${
                      item.type === 'kyc_review' ? 'bg-blue-100 text-blue-700' :
                      item.type === 'ban_appeal' ? 'bg-purple-100 text-purple-700' :
                      item.type === 'dispute' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {item.type === 'kyc_review' ? 'KYC Review' :
                       item.type === 'ban_appeal' ? 'Ban Appeal' :
                       item.type === 'dispute' ? 'Dispute' :
                       item.type}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 break-words">{item.title}</p>
                      <p className="text-sm text-gray-600 [overflow-wrap:anywhere]">{item.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 pl-0 sm:pl-3">
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                    </span>
                    <span className="text-orange-500 font-semibold text-sm whitespace-nowrap">Take Action →</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Platform Revenue Details */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8" data-testid="revenue-details">
          <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
            <h2 className="text-xl font-bold text-gray-800">
              Platform Revenue Details
              {dateRange.preset !== 'all' && (
                <span className="text-sm font-normal text-blue-600 ml-2">({dateRange.start} - {dateRange.end})</span>
              )}
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadRevenuePDF}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition"
                data-testid="download-pdf-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                PDF
              </button>
              <button
                onClick={downloadRevenueXLSX}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition"
                data-testid="download-xlsx-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                XLSX
              </button>
            </div>
          </div>

          {/* Revenue Summary Cards */}
          {revenueDetails.summary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-600 font-medium">Completed Orders</p>
                <p className="text-lg font-bold text-blue-800">{revenueDetails.summary.total_bookings || 0}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-600 font-medium">Service Amount</p>
                <p className="text-lg font-bold text-gray-800">PHP {(revenueDetails.summary.total_service_amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                <p className="text-xs text-orange-600 font-medium">Client Fees</p>
                <p className="text-lg font-bold text-orange-800">PHP {(revenueDetails.summary.total_client_fees || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-xs text-purple-600 font-medium">Provider Fees</p>
                <p className="text-lg font-bold text-purple-800">PHP {(revenueDetails.summary.total_provider_fees || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-xs text-green-600 font-medium">Platform Revenue</p>
                <p className="text-lg font-bold text-green-800">PHP {(revenueDetails.summary.total_platform_revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
              </div>
            </div>
          )}

          {/* Revenue Details Table */}
          {revenueDetails.details?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Service Amt</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Client Fee</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Provider Fee</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Platform Rev</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {revenueDetails.details.map((d, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">{d.scheduled_date || d.date?.substring(0, 10)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500 font-mono text-xs">{d.booking_order_code || d.booking_id?.substring(0, 8)}</td>
                      <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-800">{d.provider_name}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">{d.client_name}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">{d.service_title}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-gray-800">PHP {d.service_amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-orange-600">
                        PHP {d.client_fee.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        <span className="text-xs text-orange-400 ml-1">({d.client_fee_rate ?? ''}%)</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-purple-600">
                        PHP {d.provider_fee.toLocaleString(undefined, {minimumFractionDigits: 2})}
                        <span className="text-xs text-purple-400 ml-1">({d.provider_fee_rate ?? ''}%)</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right font-bold text-green-700">PHP {d.platform_revenue.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {revenueDetails.has_more && (
                <div className="text-center mt-4">
                  <button
                    onClick={loadMoreRevenue}
                    disabled={loadingMoreRevenue}
                    className="px-5 py-2 rounded-full border border-blue-200 text-blue-600 text-sm font-semibold hover:bg-blue-50 disabled:opacity-50 transition"
                    data-testid="revenue-load-more"
                  >
                    {loadingMoreRevenue ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No completed revenue data{dateRange.preset !== 'all' ? ' for the selected date range' : ''}.
            </div>
          )}
        </div>

        {/* Fee Rate Change History / Audit Trail */}
        {feeHistory.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-8 border-l-4 border-amber-500" data-testid="fee-history-section">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Fee Rate Change History
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-amber-50 border-b border-amber-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-amber-700 uppercase">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-amber-700 uppercase">Changed By</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-amber-700 uppercase">Client Fee</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-amber-700 uppercase">Provider Fee</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-amber-700 uppercase">Active Bookings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {feeHistory.map((entry, idx) => (
                    <tr key={entry.id || idx} className="hover:bg-amber-50/30">
                      <td className="px-4 py-2.5 whitespace-nowrap text-gray-700">
                        {new Date(entry.changed_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap font-medium text-gray-800">
                        {entry.changed_by_name}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-center">
                        <span className="text-gray-400 line-through mr-1">{entry.old_client_fee_rate ?? '—'}%</span>
                        <svg className="w-3 h-3 inline text-amber-500 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                        <span className="font-semibold text-orange-600">{entry.new_client_fee_rate ?? '—'}%</span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-center">
                        <span className="text-gray-400 line-through mr-1">{entry.old_provider_fee_rate ?? '—'}%</span>
                        <svg className="w-3 h-3 inline text-amber-500 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                        <span className="font-semibold text-purple-600">{entry.new_provider_fee_rate ?? '—'}%</span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-center text-gray-600">
                        {entry.active_bookings_at_change ?? 0} bookings
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}


        {/* Additional Stats */}
        {platformStats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-gray-500 text-sm font-medium">Total Services</h3>
              <p className="text-2xl font-bold text-gray-800 mt-2">{platformStats.total_services}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-gray-500 text-sm font-medium">Total Bookings</h3>
              <p className="text-2xl font-bold text-gray-800 mt-2">{platformStats.total_bookings}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-gray-500 text-sm font-medium">Avg Commission Per Transaction</h3>
              <p className="text-2xl font-bold text-gray-800 mt-2">
                {platformStats.total_bookings > 0 
                  ? `${platformStats.currency || 'PHP'} ${(platformStats.total_revenue / platformStats.total_bookings).toFixed(2)}`
                  : 'N/A'
                }
              </p>
            </div>
          </div>
        )}

        {/* Withdrawal Requests */}
        <div id="pending-withdrawals" className="bg-white p-6 rounded-lg shadow-md" data-testid="withdrawal-requests">
          <h2 className="text-xl font-bold mb-2">Pending Withdrawal Requests</h2>
          <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="manual-payout-notice">
            <strong>Manual payouts:</strong> The platform does not auto-send money. Approving acknowledges the request; you must send the funds yourself via GCash/Maya/Bank, then click <strong>Mark as Paid</strong> (with the reference number). Only then is it counted as "Withdrawn".
          </div>
          {withdrawals.filter(w => w.status === 'pending').length === 0 ? (
            <p className="text-gray-500 text-center py-8">No pending withdrawal requests</p>
          ) : (
            <div className="space-y-4">
              {withdrawals
                .filter(w => w.status === 'pending')
                .map((withdrawal) => (
                  <div key={withdrawal.id} className="border rounded-lg p-4" data-testid={`withdrawal-${withdrawal.id}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-bold text-lg">
                            {withdrawal.currency} {withdrawal.amount.toFixed(2)}
                          </span>
                          <span className="px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-700">
                            {withdrawal.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">
                          <strong>Provider:</strong> {withdrawal.user_name}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Method:</strong> {withdrawal.payment_method}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Account:</strong> {withdrawal.account_name} - {withdrawal.account_number}
                          {withdrawal.bank_name && ` (${withdrawal.bank_name})`}
                        </p>
                        <p className="text-sm text-gray-500">
                          Requested: {new Date(withdrawal.created_at).toLocaleDateString()}
                        </p>
                        {withdrawal.notes && (
                          <p className="text-sm text-gray-500 mt-1 italic">{withdrawal.notes}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => handleWithdrawalAction(withdrawal.id, 'approved')}
                          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 text-sm font-semibold"
                          data-testid={`approve-${withdrawal.id}`}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleMarkPaid(withdrawal.id)}
                          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-semibold"
                          data-testid={`mark-paid-${withdrawal.id}`}
                        >
                          Mark as Paid
                        </button>
                        <button
                          onClick={() => handleWithdrawalAction(withdrawal.id, 'rejected')}
                          className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 text-sm font-semibold"
                          data-testid={`reject-${withdrawal.id}`}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Awaiting Payout (approved manual + in-flight PayMongo 'processing') */}
        {allWithdrawals.filter(w => w.status === 'approved' || w.status === 'processing').length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-md mt-8" data-testid="awaiting-payout-section">
            <h2 className="text-xl font-bold mb-2">Awaiting / In-Progress Payouts</h2>
            <p className="text-sm text-gray-500 mb-6">Approved manual requests — send the money, then Mark as Paid. PayMongo automated payouts show as <strong>processing</strong>: use <strong>Sync Status</strong> to reconcile a stuck one, or resolve it manually with Mark as Paid / Mark as Failed.</p>
            <div className="space-y-4">
              {allWithdrawals.filter(w => w.status === 'approved' || w.status === 'processing').map((withdrawal) => {
                const isAuto = withdrawal.status === 'processing' && !!withdrawal.paymongo_payout_id;
                return (
                <div key={withdrawal.id} className="border rounded-lg p-4" data-testid={`awaiting-withdrawal-${withdrawal.id}`}>
                  <div className="flex flex-col sm:flex-row justify-between gap-3 sm:items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className="font-bold text-lg">{withdrawal.currency} {withdrawal.amount.toFixed(2)}</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${isAuto ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {isAuto ? 'processing (PayMongo)' : 'processing (manual)'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700"><strong>Provider:</strong> {withdrawal.user_name}</p>
                      <p className="text-sm text-gray-600"><strong>Method:</strong> {withdrawal.payment_method}</p>
                      <p className="text-sm text-gray-600"><strong>Account:</strong> {withdrawal.account_name} - {withdrawal.account_number}{withdrawal.bank_name && ` (${withdrawal.bank_name})`}</p>
                      {isAuto && (
                        <p className="text-xs text-gray-500 mt-1">
                          PayMongo ref: <span className="font-mono">{withdrawal.paymongo_payout_id}</span>
                          {withdrawal.payout_status && <span> · status: {withdrawal.payout_status}</span>}
                        </p>
                      )}
                      {withdrawal.payout_error && (
                        <p className="text-xs text-red-500 mt-1">Last payout error — review and resolve manually.</p>
                      )}
                    </div>
                    <div className="flex flex-row sm:flex-col gap-2 flex-wrap">
                      {isAuto && (
                        <button
                          onClick={() => handleSyncStatus(withdrawal.id)}
                          className="bg-amber-500 text-white px-4 py-2 rounded hover:bg-amber-600 text-sm font-semibold"
                          data-testid={`sync-status-${withdrawal.id}`}
                        >
                          Sync Status
                        </button>
                      )}
                      <button
                        onClick={() => handleMarkPaid(withdrawal.id)}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-semibold"
                        data-testid={`awaiting-mark-paid-${withdrawal.id}`}
                      >
                        Mark as Paid
                      </button>
                      <button
                        onClick={() => handleMarkFailed(withdrawal.id)}
                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 text-sm font-semibold"
                        data-testid={`mark-failed-${withdrawal.id}`}
                      >
                        Mark as Failed
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* All Withdrawals History */}
        <div className="bg-white p-6 rounded-lg shadow-md mt-8">
          <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
            <h2 className="text-xl font-bold">All Withdrawal History</h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Filter:</span>
              <input
                type="date"
                value={withdrawalDateRange.start}
                onChange={(e) => setWithdrawalDateRange({ start: e.target.value, end: withdrawalDateRange.end || e.target.value, preset: 'custom' })}
                className="border rounded-md px-2 py-1 text-xs"
              />
              <span className="text-gray-400 text-xs">to</span>
              <input
                type="date"
                value={withdrawalDateRange.end}
                onChange={(e) => setWithdrawalDateRange({ start: withdrawalDateRange.start || e.target.value, end: e.target.value, preset: 'custom' })}
                className="border rounded-md px-2 py-1 text-xs"
              />
              {withdrawalDateRange.start && (
                <button
                  onClick={() => setWithdrawalDateRange({ start: '', end: '', preset: 'all' })}
                  className="text-xs text-red-500 hover:text-red-700 font-medium"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          {(() => {
            const filtered = allWithdrawals.filter(w => {
              if (!withdrawalDateRange.start && !withdrawalDateRange.end) return true;
              const wDate = w.created_at?.substring(0, 10);
              if (withdrawalDateRange.start && wDate < withdrawalDateRange.start) return false;
              if (withdrawalDateRange.end && wDate > withdrawalDateRange.end) return false;
              return true;
            });
            return filtered.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No withdrawal history{withdrawalDateRange.start ? ' for the selected date range' : ''}.</p>
            ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Processed</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map((withdrawal) => (
                    <tr key={withdrawal.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(withdrawal.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {withdrawal.user_name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {withdrawal.currency} {(withdrawal.amount || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {withdrawal.payment_method}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          withdrawal.status === 'paid' || withdrawal.status === 'completed' ? 'bg-green-100 text-green-800' :
                          withdrawal.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                          withdrawal.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {withdrawal.status === 'approved' ? 'processing' : withdrawal.status}
                        </span>
                        {withdrawal.status === 'failed' && (withdrawal.payout_failure_code || withdrawal.payout_failure_message) && (
                          <p className="text-xs text-red-500 mt-1" data-testid={`withdrawal-failure-reason-${withdrawal.id}`}>
                            {withdrawal.payout_failure_code === 'AC06' || /block/i.test(withdrawal.payout_failure_message || '')
                              ? `${withdrawal.payout_failure_code || 'BlockedAccount'} · recipient ${withdrawal.payment_method} unverified/blocked`
                              : `${withdrawal.payout_failure_code || ''} ${withdrawal.payout_failure_message || ''}`.trim()}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                        {withdrawal.reference_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {withdrawal.paid_at ? new Date(withdrawal.paid_at).toLocaleDateString() : (withdrawal.processed_at ? new Date(withdrawal.processed_at).toLocaleDateString() : '-')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          })()}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
